export const RESUME_ALLOWED_TYPES = ['.doc', '.docx', '.pdf'];
export const RESUME_MAX_SIZE_MB = 20;
export const RESUME_MAX_SIZE_BYTES = RESUME_MAX_SIZE_MB * 1024 * 1024; // 20MB in bytes

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates if a file meets the criteria for resume upload
 * @param file - File to be validated
 * @returns Validation result with isValid flag and error message if applicable
 */
export const validateResumeFile = (file: File): FileValidationResult => {
  const fileName = file.name.toLowerCase();
  const hasValidExtension = RESUME_ALLOWED_TYPES.some((type) =>
    fileName.endsWith(type.toLowerCase()),
  );

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: `File format not allowed. Please use only: ${RESUME_ALLOWED_TYPES.join(', ')}`,
    };
  }

  if (file.size > RESUME_MAX_SIZE_BYTES) {
    return {
      isValid: false,
      error: `File too large. Maximum allowed size is ${RESUME_MAX_SIZE_MB}MB`,
    };
  }

  return { isValid: true };
};

/**
 * Helper function to be used with yup
 * @param value - Field value (can be File or string)
 * @param isOptional - Whether the field is optional
 * @returns true if valid, or throws Error if invalid
 */
export const yupResumeFileValidator = (value: unknown, isOptional = false) => {
  if (isOptional && (!value || typeof value === 'string')) {
    return true;
  }

  if (!value || typeof value === 'string') {
    throw new Error('Please select a file');
  }

  if (value instanceof File) {
    const validation = validateResumeFile(value);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }
  }

  return true;
};
